import type { ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

import type { Contact } from '@/types/contacts';
import {
  columnCount,
  visibleColumns,
  type ColVis,
  type SortField,
} from '@/lib/contacts';
import { ContactRow } from './ContactRow';
import { PaginationFooter } from './PaginationFooter';

interface Pagination {
  visibleCount: number;
  canLoadMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
}

interface Props {
  rows: Contact[];
  colVis: ColVis;
  sortField: SortField;
  sortOrder: 'asc' | 'desc';
  onSort: (field: SortField) => void;
  expandedId: string | null;
  onToggleExpand: (id: string) => void;
  emptyHint: string;
  pagination: Pagination;
}

// Desktop table card. Columns come from CONTACTS_COLUMNS (single source) so the
// header, colSpan, and skeleton can't drift apart.
export function ContactsTable({
  rows,
  colVis,
  sortField,
  sortOrder,
  onSort,
  expandedId,
  onToggleExpand,
  emptyHint,
  pagination,
}: Props) {
  const cols = visibleColumns(colVis);
  return (
    <div className="hidden overflow-hidden rounded-3xl border border-dark/5 bg-card shadow-card md:block">
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="border-b-2 border-dark bg-white text-left text-sm font-bold uppercase tracking-widest text-dark">
              {cols.map((col) => {
                const active = col.sortable && sortField === col.id;
                return (
                  <Th
                    key={col.id}
                    active={active}
                    sortDirection={active ? sortOrder : undefined}
                    onClick={
                      col.sortable
                        ? () => onSort(col.id as SortField)
                        : undefined
                    }
                  >
                    {col.label}
                  </Th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columnCount(colVis)}
                  className="px-6 py-12 text-center text-sm text-muted-foreground"
                >
                  {emptyHint}
                </td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <ContactRow
                  key={row.id}
                  contact={row}
                  isFirst={i === 0}
                  isExpanded={expandedId === row.id}
                  onToggleExpand={() => onToggleExpand(row.id)}
                  colVis={colVis}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      <PaginationFooter {...pagination} />
    </div>
  );
}

function Th({
  children,
  active = false,
  sortDirection,
  onClick,
}: {
  children: ReactNode;
  active?: boolean;
  sortDirection?: 'asc' | 'desc';
  onClick?: () => void;
}) {
  const interactive = !!onClick;
  return (
    <th className="whitespace-nowrap px-6 py-4 font-bold">
      {interactive ? (
        // `uppercase` repeated here because Chrome's UA stylesheet sets
        // text-transform: none on <button>, overriding the cascade from the
        // parent <tr>.
        <button
          type="button"
          onClick={onClick}
          className="inline-flex items-center gap-1.5 uppercase transition-colors hover:text-brand"
        >
          {children}
          <SortChevron active={active} direction={sortDirection} />
        </button>
      ) : (
        // Non-sortable header: no chevron — a visible chevron would imply
        // sortability we don't deliver.
        <span className="inline-flex items-center gap-1.5">{children}</span>
      )}
    </th>
  );
}

function SortChevron({
  active,
  direction = 'desc',
}: {
  active: boolean;
  direction?: 'asc' | 'desc';
}) {
  if (active) {
    const Icon = direction === 'asc' ? ChevronUp : ChevronDown;
    return <Icon className="h-3.5 w-3.5 text-brand" strokeWidth={2.5} />;
  }
  // Inactive — paired up-down chevron (muted). Inline SVG since lucide doesn't
  // ship the two-arrow shape.
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3.5 w-3.5 text-muted-foreground/50"
    >
      <polyline points="7 15 12 20 17 15" />
      <polyline points="7 9 12 4 17 9" />
    </svg>
  );
}
